import React, { useState, useEffect, useCallback } from 'react';
import { docTemplatesAPI, docCategoriesAPI, mergeFieldsAPI, customFieldsAPI, casesAPI } from '../services/api';
import PageHeader from '../components/PageHeader';
import Modal from '../components/Modal';
import {
  FileText, Plus, Trash2, Download, Wand2, Copy, Check,
  FolderOpen, Settings, ChevronDown, ChevronRight, Edit2, Upload,
  AlertCircle, Search,
} from 'lucide-react';
import { toast } from 'react-toastify';

const TABS = [
  { id: 'templates',   label: 'Templates',          icon: FileText },
  { id: 'mergefields', label: 'Merge Fields',        icon: Copy },
  { id: 'categories',  label: 'Categories',          icon: FolderOpen },
  { id: 'customfields',label: 'Custom Fields',       icon: Settings },
];

// ── Copy-to-clipboard button ─────────────────────────────────────────────────
function CopyBtn({ text }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };
  return (
    <button onClick={copy} title="Copy merge field"
      className="p-1 rounded hover:bg-gray-100 text-gray-400 hover:text-blue-600 transition-colors">
      {copied ? <Check size={13} className="text-green-600" /> : <Copy size={13} />}
    </button>
  );
}

// ── File-type badge ───────────────────────────────────────────────────────────
const TYPE_STYLE = {
  docx: 'bg-blue-100 text-blue-700',
  xlsx: 'bg-green-100 text-green-700',
  pdf:  'bg-red-100  text-red-700',
};

export default function Templates() {
  const [tab, setTab] = useState('templates');

  // ── Templates state ───────────────────────────────────────────────────────
  const [templates,   setTemplates]   = useState([]);
  const [categories,  setCategories]  = useState([]);
  const [cases,       setCases]       = useState([]);
  const [loading,     setLoading]     = useState(false);
  const [uploadModal, setUploadModal] = useState(false);
  const [editModal,   setEditModal]   = useState(false);
  const [editTarget,  setEditTarget]  = useState(null);
  const [genModal,    setGenModal]    = useState(false);
  const [genTemplate, setGenTemplate] = useState(null);
  const [genCaseId,   setGenCaseId]   = useState('');
  const [generating,  setGenerating]  = useState(false);
  const [saving,      setSaving]      = useState(false);
  const [uploadFile,  setUploadFile]  = useState(null);
  const [tForm,       setTForm]       = useState({ name: '', category: '', description: '' });
  const [searchT,     setSearchT]     = useState('');

  // ── Merge Fields state ────────────────────────────────────────────────────
  const [mfCaseId,    setMfCaseId]    = useState('');
  const [mfData,      setMfData]      = useState(null);
  const [mfLoading,   setMfLoading]   = useState(false);
  const [openCats,    setOpenCats]    = useState({});

  // ── Categories state ──────────────────────────────────────────────────────
  const [catForm,     setCatForm]     = useState({ name: '', description: '' });
  const [catSaving,   setCatSaving]   = useState(false);

  // ── Custom Fields state ───────────────────────────────────────────────────
  const [customFields, setCustomFields] = useState([]);
  const [cfForm,       setCfForm]       = useState({ name: '', field_key: '', field_type: 'text', applies_to: 'case', dropdown_options: '', is_required: false });
  const [cfModal,      setCfModal]      = useState(false);
  const [cfSaving,     setCfSaving]     = useState(false);

  // ── Loaders ───────────────────────────────────────────────────────────────
  const loadTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const [t, cat, c] = await Promise.all([
        docTemplatesAPI.list(),
        docCategoriesAPI.list(),
        casesAPI.list({ page_size: 200 }),
      ]);
      setTemplates(t.data.results || t.data);
      setCategories(cat.data.results || cat.data);
      setCases(c.data.results || c.data);
    } finally { setLoading(false); }
  }, []);

  const loadMergeFields = useCallback(async (caseId) => {
    setMfLoading(true);
    try {
      const { data } = await mergeFieldsAPI.get(caseId);
      setMfData(data);
      // open all categories by default
      const initial = {};
      Object.keys(data.categories || {}).forEach(k => { initial[k] = true; });
      setOpenCats(initial);
    } finally { setMfLoading(false); }
  }, []);

  const loadCustomFields = useCallback(async () => {
    const { data } = await customFieldsAPI.list();
    setCustomFields(data.results || data);
  }, []);

  useEffect(() => { loadTemplates(); }, [loadTemplates]);
  useEffect(() => {
    if (tab === 'mergefields') loadMergeFields(mfCaseId);
    if (tab === 'customfields') loadCustomFields();
  }, [tab]);

  // ── Template CRUD ─────────────────────────────────────────────────────────
  const handleUpload = async (e) => {
    e.preventDefault();
    if (!uploadFile) return toast.error('Select a file first');
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('file', uploadFile);
      fd.append('name', tForm.name || uploadFile.name.replace(/\.[^.]+$/, ''));
      if (tForm.category) fd.append('category', tForm.category);
      if (tForm.description) fd.append('description', tForm.description);
      await docTemplatesAPI.create(fd);
      toast.success('Template uploaded');
      setUploadModal(false);
      setUploadFile(null);
      setTForm({ name: '', category: '', description: '' });
      loadTemplates();
    } catch { toast.error('Upload failed'); }
    finally { setSaving(false); }
  };

  const handleEdit = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      const fd = new FormData();
      fd.append('name', tForm.name);
      if (tForm.category) fd.append('category', tForm.category);
      if (tForm.description) fd.append('description', tForm.description);
      if (uploadFile) fd.append('file', uploadFile);
      await docTemplatesAPI.update(editTarget.id, fd);
      toast.success('Template updated');
      setEditModal(false);
      setUploadFile(null);
      loadTemplates();
    } catch { toast.error('Update failed'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete template "${name}"? This cannot be undone.`)) return;
    await docTemplatesAPI.delete(id);
    toast.success('Template deleted');
    loadTemplates();
  };

  const handleGenerate = async () => {
    if (!genCaseId) return toast.error('Select a case first');
    setGenerating(true);
    try {
      const res = await docTemplatesAPI.generate(genTemplate.id, genCaseId);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a   = document.createElement('a');
      a.href = url;
      a.download = `${genTemplate.name}_filled.${genTemplate.file_type}`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Document generated and downloaded');
      setGenModal(false);
    } catch { toast.error('Generation failed — check that merge fields are correct'); }
    finally { setGenerating(false); }
  };

  const handleDownloadRaw = async (t) => {
    try {
      const res = await docTemplatesAPI.downloadRaw(t.id);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${t.name}.${t.file_type}`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Template downloaded');
    } catch { toast.error('Download failed'); }
  };

  // ── Category CRUD ─────────────────────────────────────────────────────────
  const handleAddCategory = async (e) => {
    e.preventDefault(); setCatSaving(true);
    try {
      await docCategoriesAPI.create(catForm);
      toast.success('Category created');
      setCatForm({ name: '', description: '' });
      loadTemplates();
    } catch { toast.error('Failed to create category'); }
    finally { setCatSaving(false); }
  };

  const handleDeleteCategory = async (id, name) => {
    if (!window.confirm(`Delete category "${name}"?`)) return;
    await docCategoriesAPI.delete(id);
    toast.success('Deleted');
    loadTemplates();
  };

  // ── Custom Fields CRUD ────────────────────────────────────────────────────
  const handleAddCustomField = async (e) => {
    e.preventDefault(); setCfSaving(true);
    try {
      await customFieldsAPI.create(cfForm);
      toast.success('Custom field created');
      setCfModal(false);
      setCfForm({ name: '', field_key: '', field_type: 'text', applies_to: 'case', dropdown_options: '', is_required: false });
      loadCustomFields();
      // Reload merge fields to include new field
      if (tab === 'mergefields') loadMergeFields(mfCaseId);
    } catch (err) {
      const msg = err.response?.data ? JSON.stringify(err.response.data) : 'Failed';
      toast.error(msg);
    } finally { setCfSaving(false); }
  };

  const handleDeleteCustomField = async (id, name) => {
    if (!window.confirm(`Delete custom field "${name}"?`)) return;
    await customFieldsAPI.delete(id);
    toast.success('Deleted');
    loadCustomFields();
  };

  // ── Visible templates (search) ────────────────────────────────────────────
  const visibleTemplates = templates.filter(t =>
    !searchT || t.name.toLowerCase().includes(searchT.toLowerCase()) ||
    t.description?.toLowerCase().includes(searchT.toLowerCase())
  );

  return (
    <div className="p-6">
      <PageHeader
        title="Document Templates"
        subtitle="Create standardized documents with merge fields"
        actions={
          tab === 'templates' ? (
            <button className="btn-primary flex items-center gap-2"
              onClick={() => { setTForm({ name: '', category: '', description: '' }); setUploadFile(null); setUploadModal(true); }}>
              <Upload size={15} /> New Template
            </button>
          ) : tab === 'customfields' ? (
            <button className="btn-primary flex items-center gap-2" onClick={() => setCfModal(true)}>
              <Plus size={15} /> New Custom Field
            </button>
          ) : null
        }
      />

      {/* ── Tab bar ──────────────────────────────────────────────────────────── */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="flex gap-0">
          {TABS.map(({ id, label, icon: Icon }) => (
            <button key={id} onClick={() => setTab(id)}
              className={`flex items-center gap-2 px-5 py-2.5 text-sm font-medium border-b-2 transition-colors
                ${tab === id ? 'border-blue-600 text-blue-700' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
              <Icon size={14} /> {label}
            </button>
          ))}
        </nav>
      </div>

      {/* ══ TEMPLATES TAB ═════════════════════════════════════════════════════ */}
      {tab === 'templates' && (
        <div>
          {/* Info banner */}
          <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 mb-5 flex gap-3">
            <AlertCircle size={18} className="text-blue-500 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-blue-700">
              <p className="font-semibold mb-1">How to use templates</p>
              <ol className="list-decimal list-inside space-y-0.5 text-blue-600 text-xs">
                <li>Go to <strong>Merge Fields</strong> tab to see the available merge codes</li>
                <li>Prepare a Word (.docx) file and insert merge fields like <code className="bg-blue-100 px-1 rounded">{'<<Matter.Client.Name>>'}</code> in place of real data</li>
                <li>Upload the file here as a template</li>
                <li>Click <strong>Generate</strong> on any template, choose a case, and download the filled document</li>
              </ol>
            </div>
          </div>

          {/* Search */}
          <div className="relative mb-4">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input className="input pl-9 text-sm" placeholder="Search templates…"
              value={searchT} onChange={e => setSearchT(e.target.value)} />
          </div>

          {loading ? (
            <div className="text-center py-16 text-gray-400">Loading…</div>
          ) : visibleTemplates.length === 0 ? (
            <div className="card text-center py-16">
              <FileText size={44} className="text-gray-200 mx-auto mb-3" />
              <p className="text-gray-500 font-medium mb-1">No templates yet</p>
              <p className="text-gray-400 text-sm mb-4">Upload a Word document with merge fields to get started</p>
              <button className="btn-primary mx-auto" onClick={() => setUploadModal(true)}>
                <Upload size={15} /> Upload First Template
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {visibleTemplates.map(t => (
                <div key={t.id} className="card p-4 hover:shadow-md transition-shadow group">
                  {/* Header */}
                  <div className="flex items-start gap-3 mb-3">
                    <div className="w-11 h-11 bg-blue-50 rounded-xl flex items-center justify-center flex-shrink-0">
                      <FileText size={20} className="text-blue-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-gray-900 text-sm truncate">{t.name}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${TYPE_STYLE[t.file_type] || 'bg-gray-100 text-gray-600'}`}>
                          .{t.file_type}
                        </span>
                        {t.category_name && (
                          <span className="text-xs text-gray-400">{t.category_name}</span>
                        )}
                      </div>
                    </div>
                  </div>

                  {t.description && (
                    <p className="text-xs text-gray-500 mb-3 line-clamp-2">{t.description}</p>
                  )}

                  <p className="text-xs text-gray-400 mb-3">
                    Added {new Date(t.created_at).toLocaleDateString('en-GB', { day:'2-digit', month:'short', year:'numeric' })}
                    {t.created_by_name && ` by ${t.created_by_name}`}
                  </p>

                  {/* Actions */}
                  <div className="flex gap-2">
                    <button
                      className="flex-1 flex items-center justify-center gap-1.5 text-xs font-medium text-white bg-blue-700 hover:bg-blue-800 rounded-lg py-2 transition-colors"
                      onClick={() => { setGenTemplate(t); setGenCaseId(''); setGenModal(true); }}>
                      <Wand2 size={12} /> Generate
                    </button>
                    <button
                      className="p-2 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                      title="Download blank template"
                      onClick={() => handleDownloadRaw(t)}>
                      <Download size={14} />
                    </button>
                    <button
                      className="p-2 text-gray-400 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-colors"
                      title="Edit"
                      onClick={() => { setEditTarget(t); setTForm({ name: t.name, category: t.category || '', description: t.description || '' }); setUploadFile(null); setEditModal(true); }}>
                      <Edit2 size={14} />
                    </button>
                    <button
                      className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      title="Delete"
                      onClick={() => handleDelete(t.id, t.name)}>
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ══ MERGE FIELDS TAB ══════════════════════════════════════════════════ */}
      {tab === 'mergefields' && (
        <div>
          {/* Case selector */}
          <div className="card mb-5 p-4">
            <p className="text-sm font-medium text-gray-700 mb-2">
              Select a case to preview merge field values
            </p>
            <div className="flex gap-3">
              <select
                className="input flex-1"
                value={mfCaseId}
                onChange={e => { setMfCaseId(e.target.value); loadMergeFields(e.target.value); }}>
                <option value="">— No case selected —</option>
                {cases.map(c => (
                  <option key={c.id} value={c.id}>
                    {c.case_number ? `${c.case_number}: ` : ''}{c.case_title}
                  </option>
                ))}
              </select>
              {mfCaseId && (
                <button className="btn-secondary text-xs" onClick={() => { setMfCaseId(''); loadMergeFields(''); }}>
                  Clear
                </button>
              )}
            </div>
            {mfData?.case && (
              <p className="text-xs text-blue-600 mt-2">
                Showing values for: <strong>{mfData.case.case_title}</strong>
              </p>
            )}
          </div>

          {mfLoading ? (
            <div className="text-center py-12 text-gray-400">Loading merge fields…</div>
          ) : mfData ? (
            <div className="space-y-3">
              {Object.entries(mfData.categories).map(([catName, fields]) => (
                <div key={catName} className="card p-0 overflow-hidden">
                  {/* Category header */}
                  <button
                    className="w-full flex items-center justify-between px-5 py-3 bg-gray-50 hover:bg-gray-100 transition-colors text-left"
                    onClick={() => setOpenCats(prev => ({ ...prev, [catName]: !prev[catName] }))}>
                    <span className="font-semibold text-sm text-gray-800">{catName}</span>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-400 bg-white border border-gray-200 rounded-full px-2 py-0.5">
                        {fields.length}
                      </span>
                      {openCats[catName]
                        ? <ChevronDown size={14} className="text-gray-400" />
                        : <ChevronRight size={14} className="text-gray-400" />
                      }
                    </div>
                  </button>

                  {/* Fields table */}
                  {openCats[catName] && (
                    <table className="w-full text-sm">
                      <thead className="bg-white border-b border-gray-100">
                        <tr>
                          <th className="text-left px-5 py-2 text-xs font-semibold text-gray-400 uppercase w-64">Merge Field</th>
                          <th className="text-left px-5 py-2 text-xs font-semibold text-gray-400 uppercase w-48">Description</th>
                          <th className="text-left px-5 py-2 text-xs font-semibold text-gray-400 uppercase">Value</th>
                          <th className="px-3 py-2 w-10"></th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-50">
                        {fields.map(f => (
                          <tr key={f.field} className="hover:bg-blue-50/30">
                            <td className="px-5 py-2.5">
                              <code className="text-xs font-mono bg-gray-100 text-blue-700 px-1.5 py-0.5 rounded">
                                {f.field}
                              </code>
                            </td>
                            <td className="px-5 py-2.5 text-xs text-gray-500">{f.description}</td>
                            <td className="px-5 py-2.5 text-xs text-gray-800">
                              {f.value
                                ? <span className="font-medium">{f.value}</span>
                                : <span className="text-gray-300 italic">not set</span>
                              }
                            </td>
                            <td className="px-3 py-2.5">
                              <CopyBtn text={f.field} />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 text-gray-400">Click a case above to load field values</div>
          )}
        </div>
      )}

      {/* ══ CATEGORIES TAB ════════════════════════════════════════════════════ */}
      {tab === 'categories' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Add form */}
          <div className="card">
            <h3 className="font-semibold text-gray-900 mb-4">New Category</h3>
            <form onSubmit={handleAddCategory} className="space-y-3">
              <div>
                <label className="label">Category Name *</label>
                <input className="input" required value={catForm.name}
                  onChange={e => setCatForm(f => ({ ...f, name: e.target.value }))} />
              </div>
              <div>
                <label className="label">Description</label>
                <textarea className="input" rows={2} value={catForm.description}
                  onChange={e => setCatForm(f => ({ ...f, description: e.target.value }))} />
              </div>
              <button type="submit" className="btn-primary w-full justify-center" disabled={catSaving}>
                <Plus size={14} /> {catSaving ? 'Creating…' : 'Create Category'}
              </button>
            </form>
          </div>

          {/* List */}
          <div className="lg:col-span-2">
            {categories.length === 0 ? (
              <div className="card text-center py-12">
                <FolderOpen size={36} className="text-gray-200 mx-auto mb-2" />
                <p className="text-gray-400 text-sm">No categories yet</p>
              </div>
            ) : (
              <div className="card p-0 overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      {['Category', 'Templates', 'Description', ''].map(h => (
                        <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-gray-400 uppercase">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {categories.map(c => (
                      <tr key={c.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 font-medium text-gray-900">{c.name}</td>
                        <td className="px-4 py-3 text-gray-500">{c.template_count}</td>
                        <td className="px-4 py-3 text-gray-500 text-xs">{c.description || '—'}</td>
                        <td className="px-4 py-3">
                          <button className="p-1.5 text-gray-300 hover:text-red-500 rounded"
                            onClick={() => handleDeleteCategory(c.id, c.name)}>
                            <Trash2 size={14} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ══ CUSTOM FIELDS TAB ════════════════════════════════════════════════ */}
      {tab === 'customfields' && (
        <div>
          <div className="bg-amber-50 border border-amber-100 rounded-xl p-4 mb-5 flex gap-3">
            <AlertCircle size={18} className="text-amber-500 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-amber-700">
              Custom fields appear as merge fields in templates.
              A field with key <code className="bg-amber-100 px-1 rounded">value_estimate</code> becomes{' '}
              <code className="bg-amber-100 px-1 rounded">{'<<Matter.CustomField.ValueEstimate>>'}</code>.
            </p>
          </div>

          {customFields.length === 0 ? (
            <div className="card text-center py-16">
              <Settings size={40} className="text-gray-200 mx-auto mb-3" />
              <p className="text-gray-400 text-sm mb-4">No custom fields yet</p>
              <button className="btn-primary mx-auto" onClick={() => setCfModal(true)}>
                <Plus size={14} /> Create First Custom Field
              </button>
            </div>
          ) : (
            <div className="card p-0 overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b">
                  <tr>{['Field Name', 'Type', 'Applies To', 'Merge Field Key', 'Required', ''].map(h => (
                    <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-gray-400 uppercase">{h}</th>
                  ))}</tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {customFields.map(cf => {
                    const pascal = cf.field_key.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join('');
                    const prefix = cf.applies_to === 'case' ? 'Matter' : 'Client';
                    const mergeKey = `<<${prefix}.CustomField.${pascal}>>`;
                    return (
                      <tr key={cf.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 font-medium text-gray-900">{cf.name}</td>
                        <td className="px-4 py-3">
                          <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded capitalize">{cf.field_type}</span>
                        </td>
                        <td className="px-4 py-3 text-gray-500 capitalize">{cf.applies_to}</td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1">
                            <code className="text-xs font-mono bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded">{mergeKey}</code>
                            <CopyBtn text={mergeKey} />
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          {cf.is_required
                            ? <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded">Required</span>
                            : <span className="text-xs text-gray-300">—</span>
                          }
                        </td>
                        <td className="px-4 py-3">
                          <button className="p-1.5 text-gray-300 hover:text-red-500 rounded"
                            onClick={() => handleDeleteCustomField(cf.id, cf.name)}>
                            <Trash2 size={14} />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ══ MODALS ════════════════════════════════════════════════════════════ */}

      {/* Upload template */}
      <Modal isOpen={uploadModal} onClose={() => setUploadModal(false)} title="Upload Template">
        <form onSubmit={handleUpload} className="space-y-4">
          <div>
            <label className="label">File * <span className="text-gray-400 font-normal text-xs">.docx, .xlsx, .pdf</span></label>
            <input type="file" className="input" required accept=".docx,.xlsx,.pdf"
              onChange={e => {
                const f = e.target.files[0];
                setUploadFile(f);
                if (!tForm.name && f) setTForm(prev => ({ ...prev, name: f.name.replace(/\.[^.]+$/, '') }));
              }} />
          </div>
          <div>
            <label className="label">Template Name *</label>
            <input className="input" required value={tForm.name} onChange={e => setTForm(f => ({ ...f, name: e.target.value }))} />
          </div>
          <div>
            <label className="label">Category</label>
            <select className="input" value={tForm.category} onChange={e => setTForm(f => ({ ...f, category: e.target.value }))}>
              <option value="">None</option>
              {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div>
            <label className="label">Description</label>
            <textarea className="input" rows={2} value={tForm.description} onChange={e => setTForm(f => ({ ...f, description: e.target.value }))} />
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" className="btn-secondary" onClick={() => setUploadModal(false)}>Cancel</button>
            <button type="submit" className="btn-primary" disabled={saving}>{saving ? 'Uploading…' : 'Upload Template'}</button>
          </div>
        </form>
      </Modal>

      {/* Edit template */}
      <Modal isOpen={editModal} onClose={() => setEditModal(false)} title="Edit Template">
        <form onSubmit={handleEdit} className="space-y-4">
          <div>
            <label className="label">Template Name *</label>
            <input className="input" required value={tForm.name} onChange={e => setTForm(f => ({ ...f, name: e.target.value }))} />
          </div>
          <div>
            <label className="label">Category</label>
            <select className="input" value={tForm.category} onChange={e => setTForm(f => ({ ...f, category: e.target.value }))}>
              <option value="">None</option>
              {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div>
            <label className="label">Description</label>
            <textarea className="input" rows={2} value={tForm.description} onChange={e => setTForm(f => ({ ...f, description: e.target.value }))} />
          </div>
          <div>
            <label className="label">Replace file <span className="text-gray-400 font-normal text-xs">(optional — leave blank to keep existing)</span></label>
            <input type="file" className="input" accept=".docx,.xlsx,.pdf" onChange={e => setUploadFile(e.target.files[0])} />
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" className="btn-secondary" onClick={() => setEditModal(false)}>Cancel</button>
            <button type="submit" className="btn-primary" disabled={saving}>{saving ? 'Saving…' : 'Save Changes'}</button>
          </div>
        </form>
      </Modal>

      {/* Generate from template */}
      <Modal isOpen={genModal} onClose={() => setGenModal(false)} title={`Generate: ${genTemplate?.name || ''}`}>
        <div className="space-y-4">
          <div className="bg-blue-50 border border-blue-100 rounded-lg p-3 text-xs text-blue-700">
            Select a case and the system will replace all merge fields with that case's data and download the filled document.
          </div>
          <div>
            <label className="label">Case / Matter *</label>
            <select className="input" value={genCaseId} onChange={e => setGenCaseId(e.target.value)}>
              <option value="">Select a case…</option>
              {cases.map(c => (
                <option key={c.id} value={c.id}>
                  {c.case_number ? `${c.case_number}: ` : ''}{c.case_title} — {c.client_name}
                </option>
              ))}
            </select>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button className="btn-secondary" onClick={() => setGenModal(false)}>Cancel</button>
            <button className="btn-primary flex items-center gap-2" onClick={handleGenerate} disabled={generating || !genCaseId}>
              <Download size={14} /> {generating ? 'Generating…' : 'Generate & Download'}
            </button>
          </div>
        </div>
      </Modal>

      {/* New custom field */}
      <Modal isOpen={cfModal} onClose={() => setCfModal(false)} title="New Custom Field">
        <form onSubmit={handleAddCustomField} className="space-y-4">
          <div>
            <label className="label">Field Name *</label>
            <input className="input" required value={cfForm.name}
              onChange={e => {
                const name = e.target.value;
                const key  = name.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/(^_|_$)/g, '');
                setCfForm(f => ({ ...f, name, field_key: key }));
              }} />
          </div>
          <div>
            <label className="label">
              Field Key <span className="text-gray-400 font-normal text-xs">(auto-generated, must be unique)</span>
            </label>
            <input className="input font-mono text-sm" value={cfForm.field_key}
              onChange={e => setCfForm(f => ({ ...f, field_key: e.target.value }))} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">Type</label>
              <select className="input" value={cfForm.field_type} onChange={e => setCfForm(f => ({ ...f, field_type: e.target.value }))}>
                {['text','number','date','checkbox','dropdown','currency'].map(t => <option key={t} value={t} className="capitalize">{t}</option>)}
              </select>
            </div>
            <div>
              <label className="label">Applies To</label>
              <select className="input" value={cfForm.applies_to} onChange={e => setCfForm(f => ({ ...f, applies_to: e.target.value }))}>
                <option value="case">Case / Matter</option>
                <option value="client">Client</option>
              </select>
            </div>
          </div>
          {cfForm.field_type === 'dropdown' && (
            <div>
              <label className="label">Options <span className="text-gray-400 font-normal text-xs">(comma-separated)</span></label>
              <input className="input" placeholder="Option 1, Option 2, Option 3" value={cfForm.dropdown_options}
                onChange={e => setCfForm(f => ({ ...f, dropdown_options: e.target.value }))} />
            </div>
          )}
          <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
            <input type="checkbox" checked={cfForm.is_required}
              onChange={e => setCfForm(f => ({ ...f, is_required: e.target.checked }))} />
            Required field
          </label>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" className="btn-secondary" onClick={() => setCfModal(false)}>Cancel</button>
            <button type="submit" className="btn-primary" disabled={cfSaving}>{cfSaving ? 'Creating…' : 'Create Field'}</button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
